import { PinInput as APinInput } from '@ark-ui/solid/pin-input';
import { splitProps, Index, Show, createEffect, children } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { Field } from './field.tsx';
import type { ComponentProps, JSX, JSXElement } from 'solid-js';

export function PinInput(props: ComponentProps<typeof APinInput.Root>) {
	return (
		<Field name={props.name ?? ''}>
			<APinInput.Root type="alphanumeric" {...props} class="group/field flex w-full flex-col justify-start gap-1">
				<APinInput.HiddenInput />
				{props.children}
			</APinInput.Root>
		</Field>
	);
}

PinInput.Label = (props: ComponentProps<typeof APinInput.Label>) => (
	<APinInput.Label {...props} class="text-lg font-medium">
		{props.children}
		<Field.Required />
	</APinInput.Label>
);

PinInput.Control = (props: ComponentProps<typeof APinInput.Control> & { count: number }) => {
	const [internal, rest] = splitProps(props, ['count', 'autofocus']);
	const [store, setStore] = createStore<{ refs: Array<HTMLInputElement | undefined> }>({
		// oxlint-disable-next-line solid/reactivity
		refs: Array.from({ length: internal.count }, () => undefined),
	});

	createEffect(() => {
		const el = store.refs[0];
		if (props.autofocus && el) {
			setTimeout(() => el && el.focus(), 1);
		}
	});

	const handleFocus: JSX.FocusEventHandlerUnion<HTMLInputElement, FocusEvent> = (e) => {
		if (e.currentTarget.value) {
			return;
		}
		const idx = store.refs.findIndex((el) => !el?.value);
		store.refs[idx]?.focus();
	};

	return (
		<APinInput.Control {...rest} class="flex gap-x-2 font-mono text-lg slashed-zero">
			<Index each={Array.from({ length: props.count }, (_, i) => i)}>
				{(id) => (
					<APinInput.Input
						ref={(el) =>
							setStore(
								produce((s) => {
									s.refs[id()] = el;
								}),
							)
						}
						index={id()}
						onFocus={handleFocus}
						placeholder=""
						class="h-12 min-w-0 shrink-0 grow basis-12 rounded border border-zinc-300 bg-white text-center transition-all duration-100 outline-none read-only:bg-zinc-100 focus-visible:ring-4 focus-visible:ring-yellow-500/50 focus-visible:ring-offset-1 focus-visible:ring-offset-zinc-50 aria-invalid:border-rose-600 dark:border-zinc-700 dark:bg-black dark:focus-visible:ring-offset-zinc-950"
					/>
				)}
			</Index>
		</APinInput.Control>
	);
};

export function PinInputField(
	props: ComponentProps<typeof PinInput.Control> & {
		name: string;
		label: JSXElement;
		description?: JSXElement;
	} & Pick<ComponentProps<typeof APinInput.Root>, 'type' | 'otp' | 'value'>,
) {
	const [field, other, rest] = splitProps(props, ['otp', 'type', 'value'], ['label', 'description']);
	const description = children(() => other.description);
	return (
		<PinInput name={rest.name ?? ''} {...field}>
			<PinInput.Label>{other.label}</PinInput.Label>
			<PinInput.Control {...rest} />
			<Field.Errors />
			<Show when={description()}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</PinInput>
	);
}
